// src/components/mitchRoom/revenueAnalytics/SubscriberChart.js
import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Badge } from 'components/ui/badge';
import { formatPeriod } from 'utils/format';

const SERIES_CONFIG = [
  { key: 'active', label: 'Active', color: '#1072CA' },
  { key: 'new', label: 'New', color: '#59A14F' },
  { key: 'resubscribed', label: 'Resubscribed', color: '#76B7B2' },
  { key: 'churned', label: 'Churned', color: '#E15759' },
];

export default function SubscriberChart({ data }) {
  // Series toggles (same pattern as RevenueChart) — the large 'active' area can
  // drown out the small delta series, so being able to hide it matters here.
  const [visibleSeries, setVisibleSeries] = useState(
    SERIES_CONFIG.reduce((acc, s) => ({ ...acc, [s.key]: true }), {})
  );

  const toggleSeries = (key, isolate = false) => {
    setVisibleSeries((prev) => {
      if (isolate) {
        const isAlreadySolo = SERIES_CONFIG.every((s) =>
          s.key === key ? prev[s.key] : !prev[s.key]
        );
        const next = {};
        SERIES_CONFIG.forEach((s) => {
          next[s.key] = isAlreadySolo ? true : s.key === key;
        });
        return next;
      }
      return { ...prev, [key]: !prev[key] };
    });
  };

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="text-token-xl font-semibold">Subscriber Growth</h3>
        <div className="flex h-[250px] items-center justify-center">
          <p className="text-token-md text-muted-foreground">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-token-large border border-borderGrey bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-token-xl font-semibold">Subscriber Growth</h3>
        <Badge variant="info">{data.current_active} Active</Badge>
      </div>

      {/* Series toggles — click to toggle, Shift+click to isolate */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {SERIES_CONFIG.map((series) => {
          const on = visibleSeries[series.key];
          return (
            <button
              key={series.key}
              type="button"
              onClick={(e) => toggleSeries(series.key, e.shiftKey)}
              title="Click to toggle · Shift+click to isolate"
              aria-pressed={on}
              className="rounded-token-large border px-2.5 py-0.5 text-xs font-medium transition-colors"
              style={
                on
                  ? { backgroundColor: series.color, borderColor: series.color, color: '#fff' }
                  : { backgroundColor: 'transparent', borderColor: series.color, color: series.color }
              }
            >
              {series.label}
            </button>
          );
        })}
      </div>

      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="subscriberActiveGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1072CA" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#1072CA" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" vertical={false} />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} tickFormatter={formatPeriod} />
            <YAxis tick={{ fontSize: 11 }} width={45} />
            <Tooltip
              labelFormatter={formatPeriod}
              formatter={(value, name) => [
                value.toLocaleString(),
                name.charAt(0).toUpperCase() + name.slice(1),
              ]}
            />
            <ReferenceLine y={0} stroke="#DADADA" />
            {visibleSeries.active && (
              <Area
                type="monotone"
                dataKey="active"
                stroke="#1072CA"
                fillOpacity={1}
                fill="url(#subscriberActiveGradient)"
                isAnimationActive={false}
              />
            )}
            {visibleSeries.new && (
              <Area
                type="monotone"
                dataKey="new"
                stroke="#59A14F"
                fill="#59A14F"
                fillOpacity={0.3}
                name="new"
                isAnimationActive={false}
              />
            )}
            {visibleSeries.resubscribed && (
              <Area
                type="monotone"
                dataKey="resubscribed"
                stroke="#76B7B2"
                fill="#76B7B2"
                fillOpacity={0.3}
                name="resubscribed"
                isAnimationActive={false}
              />
            )}
            {visibleSeries.churned && (
              <Area
                type="monotone"
                dataKey="churned"
                stroke="#E15759"
                fill="#E15759"
                fillOpacity={0.3}
                name="churned"
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
